import { AnySessionPart, SessionEvent } from "@/mds/components/sessions/session.types";
import { get, isArray, isString } from "lodash-es";

export const parseSessionPartsFromEvent = ({
  event,
}: {
  event: SessionEvent;
}): AnySessionPart[] => {
  const parts = get(event, "data.value.parts");

  if (isArray(parts)) {
    return parts as AnySessionPart[];
  }

  return [];
};

/**
 * Builds a deterministic part id for legacy parts that do not carry one.
 *
 * The fallback is scoped to the event and part position so virtualized rows keep
 * the same identity across recalculation and streaming content updates.
 */
export const resolveSessionPartFallbackId = ({
  eventId,
  partIndex,
}: {
  eventId: string;
  partIndex: number;
}) => `fallback-part-id:${eventId}:${partIndex}`;

/**
 * Resolves the stable ID of a session event part.
 *
 * Older parts did not always include IDs. Callers that know the event position
 * should pass `fallbackPartId`; callers that only have the part object get a
 * content fallback for explicit comparisons, but should prefer context.partId
 * when rendering list keys.
 */
export const resolveSessionPartId = ({
  part,
  fallbackPartId,
}: {
  part: AnySessionPart;
  fallbackPartId?: string;
}) => {
  const partId = get(part, "id") as string | undefined;

  if (isString(partId) && partId.length > 0) {
    return partId;
  }

  if (fallbackPartId) {
    return fallbackPartId;
  }

  return `fallback-part-content:${JSON.stringify(part)}`;
};
